import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, message, Modal, Form, AutoComplete, InputNumber, Typography, Switch, DatePicker, Menu, Dropdown, Checkbox, Tooltip, Divider } from 'antd';

import { Box, Paper } from '@mui/material';
import { 
  DownloadOutlined, 
  PlusOutlined, 
  SearchOutlined, 
  FileTextOutlined, 
  EditOutlined, 
  InfoCircleOutlined, 
  DownOutlined, 
  MailOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import api from "../../../authentication/axiosInstance";
import debounce from 'lodash/debounce';
import moment from 'moment';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { saveAs } from 'file-saver';
import { generateSiteDetailsPDF } from '../../../utils/pdfUtils';
import { IconFile } from '@tabler/icons-react';
import styles from './SitesListView.module.css';

const { Option } = Select;
const { Title } = Typography;

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const SitesListView = () => {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState('default');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editForm] = Form.useForm();
  const [isClientDetailsModalVisible, setIsClientDetailsModalVisible] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isAddingNewClient, setIsAddingNewClient] = useState(false);

  const [searchRegionFilter, setSearchRegionFilter] = useState(null);
  const [searchStatusFilter, setSearchStatusFilter] = useState(null);
  const [searchClientFilter, setSearchClientFilter] = useState('');

  const [territoryManagers, setTerritoryManagers] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff/');
      setStaffList(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      message.error('Failed to fetch staff');
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites/');
      setSites(response.data || []);
      setFilteredSites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      message.error('Failed to fetch sites');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/');
      const clientData = Array.isArray(response.data) ? response.data : [];
      setClients(clientData);
      console.log(clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      message.error('Failed to fetch clients');
    }
  };
  
  const fetchTerritoryManagers = async () => {
    try {
      const response = await api.get('/staff/territory_manager_staff/');
      setTerritoryManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch Territory Managers:', error);
      message.error('Failed to fetch Territory Managers');
    }
  };
  

  useEffect(() => {
    fetchSites();
    fetchClients();
    fetchStaff();
    fetchTerritoryManagers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchText, searchRegionFilter, searchStatusFilter, searchClientFilter]);


  
  const applyFilters = () => {
    const filtered = sites.filter((site) => {
      const filterText = site.site_name.toLowerCase().includes(searchText.toLowerCase());
      const filterSiteRegion = searchRegionFilter ? site.region === searchRegionFilter : true;
      const filterSiteStatus = searchStatusFilter ? site.contract_status === searchStatusFilter : true;
      const filterTextClient = site.client_name.toLowerCase().includes(searchClientFilter.toLowerCase());
      return filterText && filterSiteRegion && filterSiteStatus && filterTextClient;
    });
    setFilteredSites(filtered);
  };

  const handleCheckboxChange = (record, field, newValue) => {
    Modal.confirm({
      title: `Are you sure you want to make this change?`,
      onOk: async () => {
        try {
          const updatedData = { [field]: newValue };
  
          const response = await api.patch(`/sites/${record.site_id}/`, updatedData);
          fetchSites();
          if (response.status === 200 || response.status === 204) {
            message.success(`Field updated successfully`);
  
            setSites((prevSites) =>
              prevSites.map((site) =>
                site.site_id === record.site_id ? { ...site, [field]: newValue } : site
              )
            );
          } else {
            console.error('Unexpected response:', response);
            message.error(`Failed to update ${field.replace('_', ' ')}`);
          }
        } catch (error) {
          console.error('Update error:', error);
          message.error(`Failed to update ${field.replace('_', ' ')}`);
        }
      },
      onCancel() {
        message.info('No changes were made');
      },
    });
  };

  const defaultColumns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 200,
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
      render: (text, record) => (
        <span>
          {text}
          <Tooltip title="View Client Details">
            <InfoCircleOutlined
              onClick={() => showClientDetailsModal(record)}
              className={styles.actionIcon}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 200,
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
    },
    {
      title: 'Contract Status',
      dataIndex: 'contract_status',
      key: 'contract_status',
      width: 150,
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 140 }}
          onChange={(value) => handleContractStatusChange(record.site_id, value)}
        >
          <Option value="Estimate">Estimate</Option>
          <Option value="Unconfirmed">Unconfirmed</Option>
          <Option value="Confirmed">Confirmed</Option>
        </Select>
      ),
    },
    {
      title: 'Territory Manager',
      dataIndex: 'assigned_territory_manager',
      key: 'assigned_territory_manager',
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className={styles.actionsCell}>
          <Tooltip title="Edit Site">
            <EditOutlined
              onClick={() => handleEdit(record)}
              className={styles.actionIcon}
            />
          </Tooltip>
          <Tooltip title="Generate Invoice">
            <FileTextOutlined
              onClick={() => handleGenerateInvoice(record)}
              className={styles.actionIcon}
            />
          </Tooltip>
          <Tooltip title="Download PDF">
            <FilePdfOutlined
              onClick={() => handleDownloadPDF(record)}
              className={styles.actionIcon}
            />
          </Tooltip>
          <Tooltip title="Delete Site">
            <DeleteOutlined
              onClick={() => handleDelete(record.site_id)}
              className={styles.actionIcon}
              style={{ color: '#dc2626' }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const contractDetailsColumns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
    },
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
    },
    {
      title: 'Contract Status',
      dataIndex: 'contract_status',
      key: 'contract_status',
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 140 }}
          onChange={(value) => handleContractStatusChange(record.site_id, value)}
        >
          <Option value="Estimate">Estimate</Option>
          <Option value="Unconfirmed">Unconfirmed</Option>
          <Option value="Confirmed">Confirmed</Option>
        </Select>
      ),
    },
    {
      title: 'Base Revenue',
      dataIndex: 'base_revenue',
      key: 'base_revenue',
      render: (value) => `$${formatNumber(value)}`,
    },
    {
      title: 'Winter Start Date',
      dataIndex: 'winter_start_date',
      key: 'winter_start_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Winter End Date',
      dataIndex: 'winter_end_date',
      key: 'winter_end_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Contract Expiration',
      dataIndex: 'contract_expiration_date',
      key: 'contract_expiration_date',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Monthly Rate (No HST)',
      dataIndex: 'monthly_rate',
      key: 'monthly_rate',
      render: (value) => `$${formatNumber(value)}`,
    },
    {
      title: 'Additional Revenue Type',
      dataIndex: 'additional_revenue_type',
      key: 'additional_revenue_type',
    },
    {
      title: 'Additional Revenue Amount',
      dataIndex: 'additional_revenue_amount',
      key: 'additional_revenue_amount',
      render: (value) => `$${formatNumber(value)}`,
    },
  ];

  const debouncedSearchSite = debounce((value) => {
    setSearchText(value);
  }, 300);

  const debouncedSearchClient = debounce((value) => {
    setSearchClientFilter(value);
  }, 300);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      let clientId = values.client;
      let clientName = '';

      // If adding a new client, create contact and client first
      if (values.client === 'new' && isAddingNewClient) {
        try {
          // First create the client
          const newClientData = {
            client_name: values.new_client_name
          };

          // Create client
          const clientResponse = await api.post('/clients/', newClientData);
          clientId = clientResponse.data.client_id;
          clientName = clientResponse.data.client_name;

          // Then create the contact through the client's contacts endpoint
          const contactData = {
            first_name: values.contact_first_name,
            last_name: values.contact_last_name,
            email: values.contact_email,
            phone: values.contact_phone,
            notes: '',
            make_primary: true
          };

          // Create contact and associate it with the client
          await api.post(`/clients/${clientId}/contacts/`, contactData);

          message.success('New client and contact created successfully');
        } catch (error) {
          console.error('Failed to create new client/contact:', error);
          if (error.response?.data) {
            const errorMessages = Object.entries(error.response.data)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            message.error(`Failed to create client/contact: ${errorMessages}`);
          } else {
            message.error('Failed to create new client/contact. Please check your input and try again.');
          }
          return;
        }
      } else {
        // Get client name for existing client
        clientName = (clients || []).find((client) => client.client_id === values.client)?.client_name || null;
      }

      const formattedValues = {
        ...values,
        client: clientId,
        client_name: clientName,
        parking_lots_sqft: parseFloat(values.parking_lots_sqft),
        walkway_sqft: parseFloat(values.walkway_sqft),
        latitude: parseFloat(parseFloat(values.latitude).toFixed(6)),
        longitude: parseFloat(parseFloat(values.longitude).toFixed(6)),
        contract_status: values.contract_status,
        base_revenue: parseFloat(values.base_revenue),
        winter_start_date: values.winter_start_date ? values.winter_start_date.format('YYYY-MM-DD') : null,
        winter_end_date: values.winter_end_date ? values.winter_end_date.format('YYYY-MM-DD') : null,
        contract_expiration_date: values.contract_expiration_date ? values.contract_expiration_date.format('YYYY-MM-DD') : null,
        monthly_rate: parseFloat(values.monthly_rate),
        additional_revenue_type: values.additional_revenue_type,
        additional_revenue_amount: parseFloat(values.additional_revenue_amount),
      };

      // Remove the new client fields from the site creation payload
      delete formattedValues.new_client_name;
      delete formattedValues.contact_first_name;
      delete formattedValues.contact_last_name;
      delete formattedValues.contact_email;
      delete formattedValues.contact_phone;

      console.log('Formatted form values before submission:', formattedValues);
      const response = await api.post('/sites/', formattedValues);
      console.log('API response:', response.data);
      message.success('Site added successfully');
      setIsModalVisible(false);
      form.resetFields();
      setIsAddingNewClient(false);
      fetchSites();
      fetchClients(); // Refresh clients list
    } catch (error) {
      console.error('Failed to add site:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        if (typeof error.response.data === 'object' && error.response.data !== null) {
          const errorMessages = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          message.error(`Failed to add site: ${errorMessages}`);
        } else {
          message.error('Failed to add site. Please check your input and try again.');
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  const extractStreetAddress = (fullAddress) => {
    // Split the address by commas
    const parts = fullAddress.split(',');
    // Return the first part (usually the street address)
    return parts[0].trim();
  };

  const handleAddressSelect = async (selectedAddress) => {
    const streetAddress = extractStreetAddress(selectedAddress);
    form.setFieldsValue({ address: streetAddress });
    try {
      const geocode = await geocodeByAddress(selectedAddress);
      const { lat, lng } = await getLatLng(geocode[0]);
      form.setFieldsValue({
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      });
    } catch (error) {
      console.error('Error', error);
      message.error('Failed to get latitude and longitude');
    }
  };

  const handleEditAddressSelect = async (selectedAddress) => {
    const streetAddress = extractStreetAddress(selectedAddress);
    editForm.setFieldsValue({ address: streetAddress });
    try {
      const geocode = await geocodeByAddress(selectedAddress);
      const { lat, lng } = await getLatLng(geocode[0]);
      editForm.setFieldsValue({
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      });
    } catch (error) {
      console.error('Error', error);
      message.error('Failed to get latitude and longitude');
    }
  };

  const validateNumber = (rule, value) => {
    if (value === '' || value === undefined) {
      return Promise.reject('Please enter a value');
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return Promise.reject('Please enter a valid number');
    }
    if (num < 0) {
      return Promise.reject('Please enter a positive number');
    }
    return Promise.resolve();
  };

  const handleContractStatusChange = async (siteId, newStatus) => {
    try {
      await api.patch(`/sites/${siteId}/`, { contract_status: newStatus });
      message.success('Contract status updated successfully');
      fetchSites(); 
    } catch (error) {
      console.error('Failed to update contract status:', error);
      message.error('Failed to update contract status');
    }
  };

  const handleGenerateInvoices = async (siteId) => {
    try {
      await api.post(`/sites/${siteId}/generate_invoice/`);
      message.success('Invoice generated successfully');
      fetchSites(); // Refresh the sites list to update the invoice status
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      message.error('Failed to generate invoice');
    }
  };

  const generateManagementPDF = async () => {
    try {
      const response = await api.get('/sites/site_supervision_pdf/', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, 'site_supervision_report.pdf');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF report');
    }
  };

  const generateManagerPDF = async (territoryManager) => {
    if (!territoryManager) {
      console.error('Territory manager is required');
      message.error('Please select a territory manager');
      return;
    }
  
    try {
      const response = await api.get(`/sites/site_supervision_pdf_for_manager/`, {
        params: { territory_manager: territoryManager },
        responseType: 'blob',
      });
  
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, `${territoryManager}_site_supervision_report.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF report for the selected territory manager');
    }
  };

  const showEditModal = (site) => {
    setEditingSite(site);
    editForm.setFieldsValue({
      ...site,
      winter_start_date: site.winter_start_date ? moment(site.winter_start_date) : null,
      winter_end_date: site.winter_end_date ? moment(site.winter_end_date) : null,
      contract_expiration_date: site.contract_expiration_date ? moment(site.contract_expiration_date) : null,
    });
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingSite(null);
    editForm.resetFields();
  };

  const handleEditSubmit = async (values) => {
    try {
      const formattedValues = {
        ...values,
        // client_name: clients.find((client) => client.client_id === values.client)?.client_name || null,
        parking_lots_sqft: parseFloat(values.parking_lots_sqft),
        walkway_sqft: parseFloat(values.walkway_sqft),
        base_revenue: parseFloat(values.base_revenue),
        winter_start_date: values.winter_start_date ? values.winter_start_date.format('YYYY-MM-DD') : null,
        winter_end_date: values.winter_end_date ? values.winter_end_date.format('YYYY-MM-DD') : null,
        contract_expiration_date: values.contract_expiration_date ? values.contract_expiration_date.format('YYYY-MM-DD') : null,
        monthly_rate: parseFloat(values.monthly_rate),
        additional_revenue_amount: parseFloat(values.additional_revenue_amount),
      };
      // console.log(formattedValues)
      await api.patch(`/sites/${editingSite.site_id}/`, formattedValues);
      message.success('Site updated successfully');
      setIsEditModalVisible(false);
      editForm.resetFields();
      fetchSites();
    } catch (error) {
      console.error('Failed to update site:', error);
      message.error('Failed to update site');
    }
  };

  const isAllocationPresent = (site) => {
    return site && (site.parking_lots_sqft_allocated > 0 || site.walkway_sqft_allocated > 0 || site.assigned_walkway_crew_bool || site.assigned_salting_route_bool);
  };

  const showClientDetailsModal = (site) => {
    setSelectedSite(site);
    setIsClientDetailsModalVisible(true);
  };

  const handleClientDetailsModalCancel = () => {
    setIsClientDetailsModalVisible(false);
    setSelectedSite(null);
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={generateManagementPDF}>
        <IconFile size={16} style={{ marginRight: 8 }} />
        Generate Site Management PDF
      </Menu.Item>
      <Menu.Divider />
        <Menu.SubMenu key="2"       
          title={
            <span>
              <IconFile size={16} style={{ marginRight: 8 }} />
              Generate Territory Manager PDF
            </span>
          }>
          {Array.from(new Set((sites || []).map((site) => site.assigned_territory_manager)))
            .filter((manager) => manager) // Exclude null or undefined values
            .map((manager) => (
              <Menu.Item
                key={manager}
                onClick={() => generateManagerPDF(manager)}
              >
                {manager}
              </Menu.Item>
            ))
          }
        </Menu.SubMenu>
      {/* Add more reports here when needed */}
    </Menu>
  );

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/sites/export_csv/', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sites_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error('Failed to export sites data');
      console.error('Export error:', error);
    }
  };

  const handleEmailTM = async () => {
    try {
      // Fetch preview data
      const previewResponse = await api.get('/sites/email_territory_managers/', {
        params: { preview_only: true },
      });
  
      if (previewResponse.status === 200) {
        const { emailed_managers, failed_emails } = previewResponse.data;
  
        // Construct the list of emails to show in the modal
        const emailList = emailed_managers && emailed_managers.length
          ? emailed_managers.join(', ')
          : "No managers available to email.";
  
        const failureList = failed_emails && failed_emails.length
          ? failed_emails.map((fail) => `${fail.manager} (${fail.reason})`).join(', ')
          : "No issues detected.";
  
        // Open the modal and display the preview
        Modal.confirm({
          title: "Are you sure?",
          content: (
            <div>
              <p>This action will email all Territory Managers with the latest reports. Do you want to proceed?</p>
              <p><b>Emails to be sent to:</b> {emailList}</p>
            </div>
          ),
          okText: "Yes, Proceed",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const finalResponse = await api.get('/sites/email_territory_managers/');
              if (finalResponse.status === 200) {
                const { emailed_managers } = finalResponse.data;
  
                const successMessage = emailed_managers.length
                  ? `Emails sent successfully to: ${emailed_managers.join(', ')}`
                  : "No emails were sent successfully.";
  
                if (emailed_managers.length) message.success(successMessage, 10);
              } else {
                message.error("Failed to send emails to Territory Managers.");
              }
            } catch (error) {
              console.error("Error sending emails to Territory Managers:", error);
              message.error("Failed to send emails. Please try again.");
            }
          },
          onCancel: () => {
            message.info("Action cancelled.");
          },
        });
      } else {
        message.error("Failed to fetch preview data. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching preview data for Territory Managers:", error);
      message.error("An error occurred while fetching preview data. Please try again.");
    }
  };
  

  const handleEdit = (record) => {
    setEditingSite(record);
    editForm.setFieldsValue({
      ...record,
      client: record.client_id,
      winter_start_date: record.winter_start_date ? moment(record.winter_start_date) : null,
      winter_end_date: record.winter_end_date ? moment(record.winter_end_date) : null,
      contract_expiration_date: record.contract_expiration_date ? moment(record.contract_expiration_date) : null,
      latitude: record.latitude?.toString(),
      longitude: record.longitude?.toString(),
      parking_lots_sqft: record.parking_lots_sqft?.toString(),
      walkway_sqft: record.walkway_sqft?.toString(),
      base_revenue: record.base_revenue?.toString(),
      monthly_rate: record.monthly_rate?.toString(),
      additional_revenue_amount: record.additional_revenue_amount?.toString(),
    });
    setIsEditModalVisible(true);
  };

  const handleGenerateInvoice = async (record) => {
    try {
      await api.post(`/sites/${record.site_id}/generate-invoice/`);
      message.success('Invoice generated successfully');
      fetchSites(); // Refresh the sites list
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      message.error('Failed to generate invoice');
    }
  };

  const handleDownloadPDF = async (record) => {
    try {
      await generateSiteDetailsPDF(record);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      message.error('Failed to download PDF');
    }
  };

  const handleDelete = (siteId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this site?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await api.delete(`/sites/${siteId}/`);
          message.success('Site deleted successfully');
          fetchSites(); // Refresh the sites list
        } catch (error) {
          console.error('Failed to delete site:', error);
          message.error('Failed to delete site');
        }
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Sites Management</h1>
          <p className={styles.subtitle}>View and manage all site locations</p>
        </div>
        <div className={styles.actionButtons}>
          <Tooltip title="Add New Site">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Export to CSV">
            <Button
              onClick={handleExportCSV}
              type="primary"
              icon={<FileExcelOutlined />}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Email Territory Managers">
            <Button
              onClick={handleEmailTM}
              type="primary"
              icon={<MailOutlined />}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip title="Toggle View Mode">
            <Switch
              checkedChildren="Contract"
              unCheckedChildren="Default"
              onChange={(checked) => setViewMode(checked ? 'contract' : 'default')}
              style={{ backgroundColor: checked => checked ? '#034832' : undefined }}
            />
          </Tooltip>
        </div>
      </div>

      <div className={styles.searchSection}>
        <Input
          placeholder="Search site name"
          prefix={<SearchOutlined style={{ color: '#6B7280' }} />}
          value={searchText}
          onChange={(e) => debouncedSearchSite(e.target.value)}
          style={{ width: '100%', maxWidth: '250px' }}
        />
        <Input
          placeholder="Search client name"
          prefix={<SearchOutlined style={{ color: '#6B7280' }} />}
          value={searchClientFilter}
          onChange={(e) => debouncedSearchClient(e.target.value)}
          style={{ width: '100%', maxWidth: '250px' }}
        />
        <Select
          placeholder="Region"
          value={searchRegionFilter}
          onChange={(value) => setSearchRegionFilter(value)}
          style={{ width: '200px' }}
          allowClear
        >
          {Array.from(new Set(sites.map((site) => site.region)))
            .map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
          ))}
        </Select>
        <Select
          placeholder="Contract Status"
          value={searchStatusFilter}
          onChange={(value) => setSearchStatusFilter(value)}
          style={{ width: '200px' }}
          allowClear
        >
          {Array.from(new Set(sites.map((site) => site.contract_status)))
            .map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
          ))}
        </Select>
      </div>

      <div className={styles.tableContainer}>
        <Table
          dataSource={filteredSites}
          columns={viewMode === 'default' ? defaultColumns : contractDetailsColumns}
          rowKey="site_id"
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} sites`,
          }}
        />
      </div>

      <Modal
        title="Add New Site"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="site_name" label="Site Name" rules={[{ required: true, message: 'Please enter the site name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="client"
              label="Client"
              rules={[{ required: true, message: 'Please select a client or add a new one' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select
                placeholder="Select a client or add new"
                onChange={(value) => {
                  setIsAddingNewClient(value === 'new');
                  if (value !== 'new') {
                    // Clear new client form fields if switching back to existing client
                    form.setFieldsValue({
                      new_client_name: undefined,
                      contact_first_name: undefined,
                      contact_last_name: undefined,
                      contact_email: undefined,
                      contact_phone: undefined
                    });
                  }
                }}
              >
                <Option value="new" style={{ fontWeight: 'bold' }}>+ Add New Client</Option>
                <Option value="" disabled><Divider style={{ margin: '4px 0' }} /></Option>
                {(clients || []).map(({ client_id, client_name }) => (
                  <Option key={client_id} value={client_id}>
                    {client_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* New Client Form Fields */}
            {isAddingNewClient && (
              <>
                <Divider orientation="left">New Client Information</Divider>
                <Form.Item
                  name="new_client_name"
                  label="Client Name"
                  rules={[{ required: true, message: 'Please enter the client name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_first_name"
                  label="Contact First Name"
                  rules={[{ required: true, message: 'Please enter the contact first name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_last_name"
                  label="Contact Last Name"
                  rules={[{ required: true, message: 'Please enter the contact last name' }]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="contact_phone"
                  label="Contact Phone"
                  rules={[
                    { required: true, message: 'Please enter the contact phone number' },
                    {
                      pattern: /^\d{10}$/,
                      message: 'Please enter a valid 10-digit phone number (numbers only)'
                    }
                  ]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input 
                    placeholder="1234567890"
                    maxLength={10}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      form.setFieldsValue({ contact_phone: value });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="contact_email"
                  label="Contact Email"
                  rules={[
                    { required: true, message: 'Please enter the contact email' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                  style={{ flex: '1 1 calc(50% - 8px)' }}
                >
                  <Input />
                </Form.Item>
              </>
            )}

            <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter the address' }]}
            style={{ flex: '1 1 calc(50% - 8px)' }}
          >
            <PlacesAutocomplete
              value={form.getFieldValue('address')}
              onChange={(value) => form.setFieldsValue({ address: value })}
              onSelect={handleAddressSelect}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <Input
                    {...getInputProps({
                      placeholder: 'Enter address',
                    })}
                  />
                  <div className="autocomplete-dropdown-container">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion) => {
                      const className = suggestion.active
                        ? 'suggestion-item--active'
                        : 'suggestion-item';
                      const style = suggestion.active
                        ? { backgroundColor: '#fafafa', cursor: 'pointer', padding: '10px' }
                        : { backgroundColor: '#ffffff', cursor: 'pointer', padding: '10px' };
                      return (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                          key={suggestion.placeId}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </Form.Item>
            <Form.Item name="region" label="Region" rules={[{ required: true, message: 'Please enter the region' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item 
              name="parking_lots_sqft" 
              label="Parking Lots Sqft" 
              rules={[
                { required: true, message: 'Please enter parking lots square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} />
            </Form.Item>
            <Form.Item 
              name="walkway_sqft" 
              label="Walkway Sqft" 
              rules={[
                { required: true, message: 'Please enter walkway square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} />
            </Form.Item>
            <Form.Item name="contract_status" label="Contract Status" rules={[{ required: true, message: 'Please select the contract status' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract status">
                <Option value="Estimate">Estimate</Option>
                <Option value="Unconfirmed">Unconfirmed</Option>
                <Option value="Confirmed">Confirmed</Option>
              </Select>
            </Form.Item>
            <Form.Item name="base_revenue" label="Base Revenue" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="winter_start_date" label="Winter Start Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="winter_end_date" label="Winter End Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="contract_expiration_date" label="Contract Expiration Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="monthly_rate" label="Monthly Rate (No HST)" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="additional_revenue_type" label="Additional Revenue Type" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="additional_revenue_amount" label="Additional Revenue Amount" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
          </div>
          <Form.Item name="latitude" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="assigned_territory_manager" label="Territory Manager" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select
              showSearch
              allowClear
              onChange={(value) => form.setFieldsValue({ assigned_territory_manager: value || null })}
              options={territoryManagers.map((manager) => ({
                value: manager.name,
                label: manager.name
              }))}
              placeholder="Select Territory Manager"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="assigned_site_supervisor" label="Site Supervisor" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select
              showSearch
              allowClear
              onChange={(value) => editForm.setFieldsValue({ assigned_site_supervisor: value || null })} // Set to null when cleared
              options={staffList.map((staff) => ({ value: staff.name }))}
              placeholder="Select Site Supervisor"
              filterOption={(inputValue, option) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="dispatch_notes" label="Dispatch Notes" style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Add Site
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Site Modal */}
      <Modal
        title="Edit Site"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={800}
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="site_name" label="Site Name" rules={[{ required: true, message: 'Please enter the site name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="client"
              label="Client"
              rules={[{ required: false, message: 'Please select a client' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select
                disabled
                placeholder="Select a client"
                options={(clients || []).map(({ client_id, client_name }) => ({
                  value: client_id,
                  label: client_name,
                }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter the address' }]}
            style={{ flex: '1 1 calc(50% - 8px)' }}
          >
            <PlacesAutocomplete
              value={editForm.getFieldValue('address')}
              onChange={(value) => editForm.setFieldsValue({ address: value })}
              onSelect={handleEditAddressSelect}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <Input
                    {...getInputProps({
                      placeholder: 'Enter address',
                    })}
                  />
                  <div className="autocomplete-dropdown-container">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion) => {
                      const className = suggestion.active
                        ? 'suggestion-item--active'
                        : 'suggestion-item';
                      const style = suggestion.active
                        ? { backgroundColor: '#fafafa', cursor: 'pointer', padding: '10px' }
                        : { backgroundColor: '#ffffff', cursor: 'pointer', padding: '10px' };
                      return (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                          key={suggestion.placeId}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </Form.Item>
            <Form.Item name="region" label="Region" rules={[{ required: true, message: 'Please enter the region' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item 
              name="parking_lots_sqft" 
              label="Parking Lots Sqft" 
              rules={[
                { required: true, message: 'Please enter parking lots square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} disabled={isAllocationPresent(editingSite)} 
              title={isAllocationPresent(editingSite) ? "Ensure site deallocations before editing" : ""}/>
            </Form.Item>
            <Form.Item 
              name="walkway_sqft" 
              label="Walkway Sqft" 
              rules={[
                { required: true, message: 'Please enter walkway square footage' },
                { validator: validateNumber }
              ]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} disabled={isAllocationPresent(editingSite)} 
              title={isAllocationPresent(editingSite) ? "Ensure site deallocations before editing" : ""}/>
            </Form.Item>
            <Form.Item name="contract_status" label="Contract Status" rules={[{ required: true, message: 'Please select the contract status' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract status">
                <Option value="Estimate">Estimate</Option>
                <Option value="Unconfirmed">Unconfirmed</Option>
                <Option value="Confirmed">Confirmed</Option>
              </Select>
            </Form.Item>
            <Form.Item name="base_revenue" label="Base Revenue" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="winter_start_date" label="Winter Start Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="winter_end_date" label="Winter End Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="contract_expiration_date" label="Contract Expiration Date" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="monthly_rate" label="Monthly Rate (No HST)" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="additional_revenue_type" label="Additional Revenue Type" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="additional_revenue_amount" label="Additional Revenue Amount" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            </Form.Item>
            <Form.Item name="assigned_territory_manager" label="Territory Manager" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select
                showSearch
                allowClear
                onChange={(value) => editForm.setFieldsValue({ assigned_territory_manager: value || null })} // Set to null when cleared
                options={territoryManagers.map((manager) => ({
                  value: manager.name,
                  label: manager.name
                }))}
                placeholder="Select Territory Manager"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="assigned_site_supervisor" label="Site Supervisor" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select
                showSearch
                allowClear
                onChange={(value) => editForm.setFieldsValue({ assigned_site_supervisor: value || null })} // Set to null when cleared
                options={staffList.map((staff) => ({ value: staff.name }))}
                placeholder="Select Site Supervisor"
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="dispatch_notes" label="Dispatch Notes" style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Update Site
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Client Details Modal */}
      <Modal
        title="Client Details"
        visible={isClientDetailsModalVisible}
        onCancel={handleClientDetailsModalCancel}
        footer={null}
        width={600}
      >
        {selectedSite && (
          <div>
            <p><strong>Client Name:</strong> {selectedSite.client_name}</p>
            <p><strong>Site Name:</strong> {selectedSite.site_name}</p>
            <p><strong>Address:</strong> {selectedSite.address}</p>
            <p><strong>Region:</strong> {selectedSite.region}</p>
            <p><strong>Contract Status:</strong> {selectedSite.contract_status}</p>
            <p><strong>Base Revenue:</strong> ${formatNumber(selectedSite.base_revenue)}</p>
            <p><strong>Winter Start Date:</strong> {selectedSite.winter_start_date ? moment(selectedSite.winter_start_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Winter End Date:</strong> {selectedSite.winter_end_date ? moment(selectedSite.winter_end_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Contract Expiration:</strong> {selectedSite.contract_expiration_date ? moment(selectedSite.contract_expiration_date).format('YYYY-MM-DD') : 'N/A'}</p>
            <p><strong>Monthly Rate (No HST):</strong> ${formatNumber(selectedSite.monthly_rate)}</p>
            <p><strong>Additional Revenue Type:</strong> {selectedSite.additional_revenue_type || 'N/A'}</p>
            <p><strong>Additional Revenue Amount:</strong> ${formatNumber(selectedSite.additional_revenue_amount)}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SitesListView;
